import React from 'react';
import { Checkbox, Input, Tooltip } from 'antd';
import styles from './ApprovalSignatureCell.module.css';

/** Shared status options for approval dropdowns across dashboards. */
export const APPROVAL_STATUS_OPTIONS = [
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Ready', label: 'Ready for Approval' },
  { value: 'Not Approved', label: 'Not Approved - Update Needed' },
  { value: 'Approved', label: 'Approved', disabled: true },
];

interface ApprovalColumnHeaderProps {
  canApprove: boolean;
  checked: boolean;
  indeterminate: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/** Reusable "Approved" column header with optional bulk-select checkbox for approvers. */
export const ApprovalColumnHeader = ({
  canApprove,
  checked,
  indeterminate,
  onChange,
  disabled = false,
}: ApprovalColumnHeaderProps) => {
  if (!canApprove) return <span>Approved</span>;
  let tooltipTitle: string;
  if (disabled) {
    tooltipTitle = 'All meters are approved';
  } else if (checked) {
    tooltipTitle = 'Deselect all ready-for-approval rows';
  } else {
    tooltipTitle = 'Select all ready-for-approval rows';
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Tooltip title={tooltipTitle}>
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </Tooltip>
      <span>Approved</span>
    </div>
  );
};

interface ApprovalSignatureCellProps {
  approved: boolean;
  signature: string | null | undefined;
  canEdit: boolean;
  username?: string | null;
  onApprovedChange: (checked: boolean) => void;
  onSignatureChange: (value: string) => void;
  /** When true, always show read-only signature text regardless of approved state */
  readOnlyOverride?: boolean;
}

/**
 * Reusable approval cell: checkbox + signature input side by side when editable,
 * read-only signature text when already approved, nothing otherwise.
 * Mirrors the "Approved" column behaviour in the Invoice Dashboard.
 */
const ApprovalSignatureCell = ({
  approved,
  signature,
  canEdit,
  username,
  onApprovedChange,
  onSignatureChange,
  readOnlyOverride = false,
}: ApprovalSignatureCellProps) => {
  if (readOnlyOverride || (!canEdit && approved)) {
    return <span className={styles.readOnly}>{signature || '—'}</span>;
  }

  if (!canEdit) {
    return null;
  }

  const displayValue = signature ?? (approved && username ? username : '');

  return (
    <div className={styles.cell}>
      <Checkbox
        checked={approved}
        onChange={(e) => {
          onApprovedChange(e.target.checked);
          if (e.target.checked && !signature && username) {
            onSignatureChange(username);
          } else if (!e.target.checked) {
            onSignatureChange('');
          }
        }}
      />
      <Input
        size="small"
        value={displayValue}
        placeholder="Approver name"
        style={{ width: 130 }}
        onFocus={() => {
          if (approved && !signature && username) {
            onSignatureChange(username);
          }
        }}
        onChange={(e) => onSignatureChange(e.target.value)}
      />
    </div>
  );
};

export default ApprovalSignatureCell;
